import { AntTag, Icons } from '@agro-club/agroclub-shared'
import { useAProgress } from 'hooks/useAProgress'
import { Bid, BidStatus } from 'modules/domain/bid/types'
import { isPurchaseBid } from 'modules/domain/bid/utils'
import { SlugLabelPair } from 'modules/domain/common/types'
import { DealStatusTypes } from 'modules/domain/deal/types'
import { UserDeal } from 'modules/domain/userBid/types'
import { endpoints } from 'modules/endpoints'
import { getMainParameter } from 'modules/utils/helpers'
import { apiClient } from 'modules/utils/httpClient'
import formatNumber from 'modules/utils/numbers/formatNumber'
import formatPrice from 'modules/utils/numbers/formatPrice'
import { formatQuantity } from 'modules/utils/numbers/formatQuantity'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { AButton, ASimpleLink } from 'views/components/Analytics'
import { DealCardPrice } from 'views/components/DealCard/DealCardPrice'
import { DeliveryPeriodData } from 'views/components/DealCard/DeliveryPeriod'
import DealRoutes from 'views/pages/Deal/routes'
import {
  CardButtonWrapper,
  CardInfo,
  CompanyName,
  FooterCard,
  IconCheck,
  ItemCard,
  Location,
  MatchCardTags,
  TextCard,
} from 'views/styled/ItemCardStyles'
import { CompanySizeTag } from '../../CompanySizeTag'
import { RemoveMatchModal } from './RemoveMatchModal'
import { ToNewModal } from 'views/components/ChangeDealStatus/ToNewModal'
import { useSingleEntity } from 'modules/domain/common/hooks'
import { SampleWithRequests } from 'views/pages/Samples/types'
import { Address } from 'modules/domain/types'

type Props = {
  onStatusChange: () => void
  reasons: SlugLabelPair[]
  match: UserDeal
  activeBid: Bid
}

export const MatchCard: FC<Props> = ({ match, activeBid, reasons, onStatusChange }) => {
  const { t } = useTranslation(['user', 'deal'])
  const [visibilityChangeModal, setVisibilityChangeModal] = useState<boolean>(false)
  const [visibilityRemoveModal, setVisibilityRemoveModal] = useState<boolean>(false)
  const [currentReason, setCurrentReason] = useState<string>()
  const bidForMatch = useMemo(() => {
    if (isPurchaseBid(activeBid)) {
      return match.sale_bid
    }
    return match.purchase_bid
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBid])

  useEffect(() => {
    if (reasons && match.closed_reason) {
      const rightReason = reasons.find((reason) => reason.slug === match.closed_reason)
      setCurrentReason(rightReason?.label)
    }
  }, [reasons, match, activeBid])

  const [fetchSamples, setFetchSamples] = useState<boolean>(false)
  const [samplesProgress, samples] = useSingleEntity<SampleWithRequests[]>(
    fetchSamples ? endpoints.samplesForBid(match.sale_bid.id) : undefined,
  )

  const [fetchCustomerAddress, setFetchCustomerAddress] = useState<boolean>(false)
  const [customerAddressProgress, customerAddress] = useSingleEntity<Address>(
    fetchCustomerAddress && match.purchase_bid.address.id
      ? endpoints.companyAddressDetails(match.purchase_bid.company.id.toString(), match.purchase_bid.address.id)
      : undefined,
  )

  const tagsColor = match.closed_at ? 'grey' : 'white'

  const tags = (
    <>
      <CompanySizeTag companySize={match.sale_bid.company.company_size} />
      {bidForMatch.owner_team && <AntTag color={tagsColor}>{bidForMatch.owner_team.name}</AntTag>}
      <AntTag color={tagsColor}>{t(`deal:statuses.${match.status}`)}</AntTag>
      {currentReason && <AntTag color="grey">{t('closedMatch', { currentReason })}</AntTag>}
    </>
  )

  const handlerChangeModal = (e) => {
    e.preventDefault()
    setFetchCustomerAddress(true)
    setVisibilityChangeModal(true)
  }

  const [, changeStatusDeal] = useAProgress(
    () => apiClient.post(endpoints.dealStatus(match.id), { new_status: DealStatusTypes.NEW }),
    {
      eventName: 'changeDealStatus',
      onSuccess: onStatusChange,
    },
  )

  const handlerRemoveModal = (e) => {
    e.preventDefault()
    setVisibilityRemoveModal(true)
    setFetchSamples(true)
  }

  return (
    <div>
      <ASimpleLink
        eventName="match"
        itemId={match.id}
        to={generatePath(DealRoutes.Details, {
          id: match.id,
        })}
      >
        <ItemCard status={match.closed_at && BidStatus.archived}>
          <MatchCardTags>{tags}</MatchCardTags>
          {match.status === DealStatusTypes.DRAFT && !match.closed_at && !match.sale_bid_has_active_deals && (
            <CardButtonWrapper>
              <AButton
                id="changeDealStatus"
                color="secondary"
                background="none"
                onClick={(e) => handlerChangeModal(e)}
                Icon={Icons.IconArrowRightM}
                progress={customerAddressProgress}
              />
            </CardButtonWrapper>
          )}
          <DealCardPrice deal={match} isClosed={!!match.closed_at} />
          <CardInfo>
            <DeliveryPeriodData data={match} />
            <TextCard>{formatQuantity(match.quantity)}, </TextCard>
            {getMainParameter(bidForMatch.parameters)}
          </CardInfo>
          <CardInfo>{`${formatPrice(match.margin, true)} (${formatNumber(match.margin_percent)}%)`}</CardInfo>
          <CompanyName>
            {`${bidForMatch.company.name}`}
            {bidForMatch.has_work_deals && <IconCheck />}
          </CompanyName>
          <FooterCard>
            <Location color="secondary">{bidForMatch.address.address}</Location>

            {match.status !== DealStatusTypes.DRAFT && !match.closed_at ? (
              <AButton
                id="removeMatch"
                color="secondary"
                background="none"
                Icon={Icons.IconDelete}
                onClick={handlerRemoveModal}
                progress={samplesProgress}
              />
            ) : null}
          </FooterCard>
        </ItemCard>
      </ASimpleLink>
      {visibilityChangeModal && customerAddress && (
        <ToNewModal
          dealId={parseInt(match.id)}
          customerCompanyId={match.purchase_bid.company.id} // number here
          onClose={() => setVisibilityChangeModal(false)}
          changeDealStatusHandler={changeStatusDeal}
          customerAddress={customerAddress}
          has_filled_unload_address_params={match.has_filled_unload_address_params}
        />
      )}

      {visibilityRemoveModal && samples && (
        <RemoveMatchModal
          onStatusChange={onStatusChange}
          onClose={() => setVisibilityRemoveModal(false)}
          matchId={match?.id}
          samples={samples}
        />
      )}
    </div>
  )
}
